import { useState } from 'react'
import { Bug, ChevronRight, Maximize2, RotateCcw, Sparkles, Zap } from "lucide-react"
import { ActivityLogLine } from "../activityLogLine/ActivityLogLine"
import { DebugEntry, DuplicateChoice, Message } from "../aiChatTypes"
import { DUPLICATE_RESOLUTION_LABELS } from '../duplicateActions'
import styles from './MessageBubble.module.css'
import { renderMarkdown } from './messageBubbleHelpers'
import DebugDetailModal from './DebugDetailModal'

function DebugExpansion({ entry, previousMessageCount }: { entry: DebugEntry; previousMessageCount: number }) {
  const [modalOpen, setModalOpen] = useState(false)
  const newCount = Math.max(entry.messages.length - previousMessageCount, 0)

  return (
    <>
      <button className={styles.debugLine} onClick={() => setModalOpen(true)}>
        <Bug size={12} className={styles.debugLineIcon} />
        <span className={styles.debugLineLabel}>View AI debug data</span>
        <span className={styles.debugLineMeta}>
          {newCount} new {newCount === 1 ? 'message' : 'messages'}
        </span>
        <Maximize2 size={11} className={styles.debugLineExpand} />
      </button>
      {modalOpen && (
        <DebugDetailModal
          entry={entry}
          previousMessageCount={previousMessageCount}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}

function JsonExpandBlock({ content }: { content: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button className={styles.jsonToggle} onClick={() => setOpen(v => !v)}>
        <ChevronRight size={11} className={`${styles.jsonChevron} ${open ? styles.jsonChevronOpen : ''}`} />
        {open ? 'Hide JSON' : 'Show JSON'}
      </button>
      {open && <pre className={styles.jsonBlock}>{content}</pre>}
    </div>
  )
}

export function MessageBubble({ msg, onUndo, onApprovalResponse, onDuplicateResponse, debugLog, debugExpanded }: {
  msg: Message
  onUndo: (id: string, prev: Record<string, unknown>) => void
  onApprovalResponse?: (approved: boolean) => void
  onDuplicateResponse?: (choice: DuplicateChoice) => void
  debugLog?: Record<string, DebugEntry>
  debugExpanded?: boolean
}) {
  if (msg.activityEvent) {
    const debugKey = msg.activityEvent.debugKey
    const debugEntry = debugExpanded && debugKey ? debugLog?.[debugKey] : undefined

    let previousMessageCount = 0
    if (debugEntry && debugLog && debugKey) {
      const keys = Object.keys(debugLog)
      const idx = keys.indexOf(debugKey)
      if (idx > 0) previousMessageCount = debugLog[keys[idx - 1]]?.messages.length ?? 0
    }
    return (
      <>
        <ActivityLogLine event={msg.activityEvent} />
        {debugEntry && <DebugExpansion entry={debugEntry} previousMessageCount={previousMessageCount} />}
      </>
    )
  }

  return (
    <div className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}>
      {msg.role === 'assistant' && (
        <span className={styles.assistantAvatar}><Sparkles size={13} color="#f9e77e" /></span>
      )}
      <div className={styles.messageBubble}>
        {msg.awaitingDuplicate ? (
          <div className={styles.approvalBlock}>
            <span className={styles.messageText}>{msg.content}</span>
            <div className={styles.duplicateButtons}>
              <button className={styles.approveBtn} onClick={() => onDuplicateResponse?.('forward')}>
                Open existing tool
              </button>
              <button className={styles.approveBtn} onClick={() => onDuplicateResponse?.('duplicate')}>
                Duplicate &amp; edit with AI
              </button>
              <button className={styles.skipBtn} onClick={() => onDuplicateResponse?.('continue')}>
                Continue anyway
              </button>
            </div>
          </div>
        ) : msg.duplicateResolution ? (
          <span className={styles.messageText}>
            {msg.content}{' '}
            <span className={styles.approvalDecisionYes}>
              {DUPLICATE_RESOLUTION_LABELS[msg.duplicateResolution]}
            </span>
          </span>
        ) : msg.awaitingApproval ? (
          <div className={styles.approvalBlock}>
            <span className={styles.messageText}>{msg.content}</span>
            <div className={styles.approvalButtons}>
              <button className={styles.approveBtn} onClick={() => onApprovalResponse?.(true)}>
                Run test
              </button>
              <button className={styles.skipBtn} onClick={() => onApprovalResponse?.(false)}>
                Skip
              </button>
            </div>
          </div>
        ) : msg.approvalGranted !== undefined ? (
          <span className={styles.messageText}>
            {msg.content}{' '}
            <span className={msg.approvalGranted ? styles.approvalDecisionYes : styles.approvalDecisionNo}>
              {msg.approvalGranted ? '- Test approved' : '- Test skipped'}
            </span>
          </span>
        ) : msg.isFieldUpdate ? (
          <div className={styles.fieldUpdateRow}>
            <span className={styles.fieldUpdateMsg}>
              <Zap size={12} style={{ display: 'inline', marginRight: 4 }} />
              {msg.content}
            </span>
            {msg.undone ? (
              <span className={styles.undoneTag}>Undone</span>
            ) : msg.previousValues && Object.keys(msg.previousValues).length > 0 ? (
              <button
                className={styles.undoBtn}
                onClick={() => onUndo(msg.id, msg.previousValues!)}
                title="Undo this change"
              >
                <RotateCcw size={11} />
                Undo
              </button>
            ) : null}
          </div>
        ) : msg.showRaw ? (
          <JsonExpandBlock content={msg.content} />
        ) : msg.toolSummary ? (
          <div>
            {msg.content && <p className={styles.toolSummaryIntro}>{msg.content}</p>}
            <div className={styles.toolCard}>
              <div className={styles.toolCardHeader}>
                {msg.toolSummary.httpMethod && (
                  <span className={styles.toolCardMethod}>{msg.toolSummary.httpMethod}</span>
                )}
                <span className={styles.toolCardName}>{msg.toolSummary.title || msg.toolSummary.name}</span>
              </div>
              {msg.toolSummary.path && <span className={styles.toolCardPath}>{msg.toolSummary.path}</span>}
              {msg.toolSummary.description && <p className={styles.toolCardDesc}>{msg.toolSummary.description}</p>}
            </div>
          </div>
        ) : (
          <span className={styles.messageText}>{renderMarkdown(msg.content)}</span>
        )}
      </div>
    </div>
  )
}
